import { supabase, getAnonymousUserId, getUserId } from '../lib/supabase';
import type { FeedbackType, FeedbackRecord } from '../types/feedback';
import { getCurrentUser } from './authService';
import { logger } from '../utils/logger';

/**
 * Submit or update feedback for a message
 * Uses UPSERT to create new feedback or update existing feedback
 */
export async function submitFeedback(
  messageId: string,
  feedbackType: FeedbackType
): Promise<{ success: boolean; error: string | null; feedback: FeedbackRecord | null }> {
  try {
    if (!messageId) {
      return {
        success: false,
        error: '消息ID不能为空',
        feedback: null,
      };
    }

    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    // Prepare feedback data
    const feedbackData: any = {
      message_id: messageId,
      feedback_type: feedbackType,
      updated_at: new Date().toISOString(),
    };

    // Set user identification
    if (isAuthenticated && user) {
      // Authenticated user: use auth_user_id
      feedbackData.auth_user_id = user.id;
      // Keep user_id as anonymous ID if it exists (for continuity)
      const anonymousUserId = getAnonymousUserId();
      feedbackData.user_id = anonymousUserId;
    } else {
      // Anonymous user: only set user_id
      feedbackData.user_id = userId;
      feedbackData.auth_user_id = null;
    }

    logger.log('💾 Submitting feedback:', {
      messageId,
      feedbackType,
      userId,
      isAuthenticated,
      hasAuthUser: !!user,
    });

    // First, check if feedback already exists
    let existingQuery;
    if (isAuthenticated && user) {
      // For authenticated users: query by auth_user_id
      existingQuery = supabase
        .from('recommendation_feedback')
        .select('id, feedback_type')
        .eq('message_id', messageId)
        .eq('auth_user_id', user.id)
        .maybeSingle();
    } else {
      // For anonymous users: query by user_id
      existingQuery = supabase
        .from('recommendation_feedback')
        .select('id, feedback_type')
        .eq('message_id', messageId)
        .eq('user_id', userId)
        .is('auth_user_id', null)
        .maybeSingle();
    }

    const { data: existing, error: queryError } = await existingQuery;

    if (queryError && queryError.code !== 'PGRST116') { // PGRST116 = no rows returned
      logger.error('❌ Error checking existing feedback:', queryError);
      return {
        success: false,
        error: queryError.message || '检查现有反馈失败',
        feedback: null,
      };
    }

    let data, error;
    if (existing) {
      // Update existing feedback
      const { data: updated, error: updateError } = await supabase
        .from('recommendation_feedback')
        .update({
          feedback_type: feedbackType,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();
      
      data = updated;
      error = updateError;
    } else {
      // Insert new feedback
      const { data: inserted, error: insertError } = await supabase
        .from('recommendation_feedback')
        .insert(feedbackData)
        .select()
        .single();
      
      data = inserted;
      error = insertError;
    }

    if (error) {
      logger.error('❌ Error submitting feedback:', error);
      return {
        success: false,
        error: error.message || '提交反馈失败',
        feedback: null,
      };
    }

    logger.log('✅ Feedback submitted successfully:', data);
    return {
      success: true,
      error: null,
      feedback: data as FeedbackRecord,
    };
  } catch (error: any) {
    logger.error('❌ Exception while submitting feedback:', error);
    return {
      success: false,
      error: error.message || '提交反馈时发生错误',
      feedback: null,
    };
  }
}

/**
 * Get current user's feedback for a specific message
 */
export async function getFeedback(
  messageId: string
): Promise<{ feedback: FeedbackRecord | null; error: string | null }> {
  try {
    if (!messageId) {
      return {
        feedback: null,
        error: '消息ID不能为空',
      };
    }

    const { userId, isAuthenticated } = await getUserId();
    const user = await getCurrentUser();

    logger.log('🔍 Getting feedback:', {
      messageId,
      userId,
      isAuthenticated,
    });

    // Query feedback based on user type
    let query;
    if (isAuthenticated && user) {
      // For authenticated users: query by auth_user_id
      query = supabase
        .from('recommendation_feedback')
        .select('*')
        .eq('message_id', messageId)
        .eq('auth_user_id', user.id)
        .maybeSingle();
    } else {
      // For anonymous users: query by user_id
      query = supabase
        .from('recommendation_feedback')
        .select('*')
        .eq('message_id', messageId)
        .eq('user_id', userId)
        .is('auth_user_id', null)
        .maybeSingle();
    }

    const { data, error } = await query;

    if (error) {
      logger.error('❌ Error getting feedback:', error);
      return {
        feedback: null,
        error: error.message || '获取反馈失败',
      };
    }

    return {
      feedback: data as FeedbackRecord | null,
      error: null,
    };
  } catch (error: any) {
    logger.error('❌ Exception while getting feedback:', error);
    return {
      feedback: null,
      error: error.message || '获取反馈时发生错误',
    };
  }
}

/**
 * Get feedback counts for a message (optional, for future use)
 * Returns the total number of good and bad feedbacks
 */
export async function getFeedbackCounts(
  messageId: string
): Promise<{ good: number; bad: number; error: string | null }> {
  try {
    if (!messageId) {
      return {
        good: 0,
        bad: 0,
        error: '消息ID不能为空',
      };
    }

    const { data: feedbacks, error } = await supabase
      .from('recommendation_feedback')
      .select('feedback_type')
      .eq('message_id', messageId);

    if (error) {
      logger.error('❌ Error getting feedback counts:', error);
      return {
        good: 0,
        bad: 0,
        error: error.message || '获取反馈统计失败',
      };
    }

    const good = feedbacks?.filter((f) => f.feedback_type === 'good').length || 0;
    const bad = feedbacks?.filter((f) => f.feedback_type === 'bad').length || 0;

    return {
      good,
      bad,
      error: null,
    };
  } catch (error: any) {
    logger.error('❌ Exception while getting feedback counts:', error);
    return {
      good: 0,
      bad: 0,
      error: error.message || '获取反馈统计时发生错误',
    };
  }
}

